import React from "react";

import cn from "classnames";

import { boxForwardRef } from "../../../../../../helpers/common/Box/Box";
import { TextCore } from "../../../../../../helpers/common/TextCore/TextCore";
import { TITLE_MIXIN_BY_HEADER_SIZE_MAP } from "../../constants/sizes";
import { createDisplayNameForCoreHeader } from "../../utils/createDisplayName";
import { createTestIdForCoreHeader } from "../../utils/createTestId";

import type { BoxComponentWithDefault } from "../../../../../../helpers/common/Box/Box";
import type {
    OmitOfUnion,
    PropsWithViewNever,
} from "../../../../../../types/react";
import type { TextProps } from "../../../../../Text";
import type { CoreHeaderSize, CoreHeaderTag } from "../../types";

import classes from "./CoreHeaderTitle.styl";

const TITLE_TEST_ID = createTestIdForCoreHeader("title");

type CoreHeaderTitleProps = PropsWithViewNever<
    OmitOfUnion<
        TextProps,
        | "alignmentX"
        | "width"
        | "lineHeight"
        | "typographyStyle"
        | "quote"
        | "asList"
    >
> & {
    size?: CoreHeaderSize;
    tag?: CoreHeaderTag;
};

export const CoreHeaderTitle = boxForwardRef<
    BoxComponentWithDefault<"span", CoreHeaderTitleProps>
>(
    (
        {
            size,
            tag,
            children,
            view,
            customVarsStyles,
            customColorsStyles,
            "data-tsid": tsid = TITLE_TEST_ID,
            ...props
        },
        ref,
    ) => {
        const titleTag = tag ?? "h2";

        const titleStyles = cn(classes["title"], classes[`__view-${view!}`]);

        return children ? (
            <TextCore
                as={titleTag}
                {...props}
                typographyStyle={size && TITLE_MIXIN_BY_HEADER_SIZE_MAP[size]}
                textContainerClassName={titleStyles}
                textClassName={classes["title"]}
                customVarsStyles={cn(customVarsStyles, customColorsStyles)}
                data-tsid={tsid}
                ref={ref}
            >
                {children}
            </TextCore>
        ) : null;
    },
);

CoreHeaderTitle.displayName = createDisplayNameForCoreHeader("Title");

export default CoreHeaderTitle;
